import React, { useState, useEffect } from 'react';
import { apiCall } from '../../utils/api';
import { AlertOctagon, Users, MapPinOff, UserX, ChevronRight, RefreshCw } from 'lucide-react';

/**
 * Data Quality Overview Card — Feb 2026.
 *
 * Lightweight snapshot for the admin dashboard hero:
 *   - duplicate contacts (email + phone)
 *   - inactive workers
 *   - shifts with missing or invalid addresses
 *
 * Clicking the card jumps into the full Data Quality tab.
 */
export default function DataQualityOverviewCard({ onOpen }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = async () => {
    setLoading(true);
    setErr('');
    try {
      const res = await apiCall('/api/admin/data-quality/summary');
      setSummary(res);
    } catch (e) {
      setErr(e?.message || 'Failed to load data quality summary');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const totalIssues = summary?.total_issues ?? 0;
  const tone = totalIssues === 0
    ? { pill: 'bg-emerald-100 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500', banner: 'from-emerald-50 to-teal-50 border-emerald-200' }
    : totalIssues < 25
      ? { pill: 'bg-amber-100 text-amber-700 border-amber-200', dot: 'bg-amber-500', banner: 'from-amber-50 to-orange-50 border-amber-200' }
      : { pill: 'bg-red-100 text-red-700 border-red-200', dot: 'bg-red-500', banner: 'from-red-50 to-rose-50 border-red-200' };

  const dupTotal = summary?.duplicate_contacts?.total ?? 0;
  const dupEmail = summary?.duplicate_contacts?.by_email ?? 0;
  const dupPhone = summary?.duplicate_contacts?.by_phone ?? 0;
  const inactive = summary?.inactive_workers ?? 0;
  const addrIssues = summary?.shifts_missing_address ?? 0;

  return (
    <div
      className={`bg-gradient-to-r ${tone.banner} border rounded-2xl p-5`}
      data-testid="data-quality-overview-card"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <AlertOctagon className="w-5 h-5 text-slate-700" />
            <h3 className="font-semibold text-slate-900">Data Quality</h3>
            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold border ${tone.pill}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${tone.dot}`}></span>
              {loading ? '…' : `${totalIssues} issue${totalIssues === 1 ? '' : 's'}`}
            </span>
          </div>
          <p className="text-sm text-slate-600">
            Duplicate contacts, inactive workers and shifts missing an address at a glance.
          </p>
          {err && !loading && (
            <p className="text-xs text-red-600 mt-2">{err}</p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={load}
            className="p-2 text-slate-500 hover:text-slate-700 hover:bg-white/60 rounded-lg transition"
            aria-label="Refresh data quality summary"
            data-testid="data-quality-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          {onOpen && (
            <button
              onClick={onOpen}
              className="flex items-center gap-1 text-sm font-medium text-slate-700 hover:text-slate-900 bg-white/70 hover:bg-white px-3 py-1.5 rounded-lg border border-white/60 transition"
              data-testid="data-quality-open-full"
            >
              Open <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4">
        <MetricTile
          testId="dq-duplicate-contacts"
          icon={<Users className="w-4 h-4 text-blue-600" />}
          label="Duplicate contacts"
          value={dupTotal}
          sub={loading ? '' : `${dupEmail} email · ${dupPhone} phone`}
          onClick={onOpen}
        />
        <MetricTile
          testId="dq-inactive-workers"
          icon={<UserX className="w-4 h-4 text-slate-600" />}
          label="Inactive workers"
          value={inactive}
          sub={inactive ? 'Archived or deactivated' : 'All active'}
          onClick={onOpen}
        />
        <MetricTile
          testId="dq-missing-addresses"
          icon={<MapPinOff className="w-4 h-4 text-rose-600" />}
          label="Shifts missing address"
          value={addrIssues}
          sub={addrIssues ? 'Location required' : 'All addressed'}
          onClick={onOpen}
        />
      </div>
    </div>
  );
}

function MetricTile({ icon, label, value, sub, onClick, testId }) {
  const clickable = typeof onClick === 'function';
  const Cmp = clickable ? 'button' : 'div';
  return (
    <Cmp
      onClick={onClick}
      className={`text-left bg-white/80 rounded-xl p-3 border border-white/70 ${clickable ? 'hover:bg-white transition' : ''}`}
      data-testid={testId}
    >
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <span className="text-xs font-medium text-slate-500 truncate">{label}</span>
      </div>
      <div className="text-2xl font-bold text-slate-900 leading-tight">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-0.5 truncate">{sub}</div>}
    </Cmp>
  );
}
